import type { ITopWin, ITopWinsResponse } from 'features/topWins';
import { useAppStore } from 'store/app/appStore';

const TOP_WIN_GAMES_PATH = '/api/history/statistics/top-win-games';

interface BeTopWin {
  gameKey: string;
  originalCurrency: string;
  /** Amount in minor units (cents) — divide by 100 for the displayed value. */
  originalAmountRaw: number;
  convertedCurrency: string;
  /** Amount in minor units (cents) — divide by 100 for the displayed value. */
  convertedAmount: number;
  /** Epoch milliseconds (UTC) of when the win completed. */
  completedDtEpochMs?: number;
}

// BE sends amounts in minor units (cents); the UI shows major units to 2 dp.
const MINOR_UNITS_PER_MAJOR = 100;

interface BeTopWinGamesResponse {
  topCasinoWin: BeTopWin | null;
  gameWins: BeTopWin[];
}

const mapBeWin = (be: BeTopWin, id: string, supportedGames: Record<string, { name?: string }>): ITopWin => ({
  id,
  gameKey: be.gameKey,
  // BE does not send a display name — resolve from the games catalog, fall back to the key.
  gameName: supportedGames[be.gameKey]?.name ?? be.gameKey,
  // BE sends epoch ms; convert to ISO. Fall back to "now" if the field is absent.
  wonAt: be.completedDtEpochMs ? new Date(be.completedDtEpochMs).toISOString() : new Date().toISOString(),
  original: { amount: be.originalAmountRaw / MINOR_UNITS_PER_MAJOR, currency: be.originalCurrency },
  // null when no conversion happened (win currency === requested display currency).
  converted:
    be.convertedCurrency !== be.originalCurrency
      ? { amount: be.convertedAmount / MINOR_UNITS_PER_MAJOR, currency: be.convertedCurrency }
      : null,
});

/** Amount shown in the UI (converted to the display currency, else original). */
const displayAmount = (win: ITopWin): number => (win.converted ?? win.original).amount;

/**
 * Collapse wins to one per game, keeping the largest. The id is the gameKey so
 * the same game keeps a stable identity across polls (positional ids made the
 * rotating queue duplicate a game when the snapshot reordered). If BE ever
 * sends the same game twice in a snapshot, only its biggest win survives.
 */
const deduplicateByGameKeepingLargest = (wins: ITopWin[]): ITopWin[] => {
  const byGame = new Map<string, ITopWin>();
  for (const win of wins) {
    const existing = byGame.get(win.gameKey);
    if (!existing || displayAmount(win) > displayAmount(existing)) {
      byGame.set(win.gameKey, win);
    }
  }
  return Array.from(byGame.values());
};

const mapTopWinsResponse = (data: BeTopWinGamesResponse, userCurrency: string): ITopWinsResponse => {
  const supportedGames = useAppStore.getState().supportedGames;

  return {
    featured: data.topCasinoWin ? mapBeWin(data.topCasinoWin, 'featured', supportedGames) : null,
    recent: deduplicateByGameKeepingLargest(
      (data.gameWins ?? []).map((win) => mapBeWin(win, win.gameKey, supportedGames)),
    ),
    refreshedAt: new Date().toISOString(),
    userCurrency,
  };
};

export const fetchTopWins = async (currency: string): Promise<ITopWinsResponse> => {
  const apiHost = useAppStore.getState().settings.apiHost;
  const url = `${apiHost}${TOP_WIN_GAMES_PATH}?currency=${encodeURIComponent(currency)}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch top wins: ${response.statusText} (URL: ${url})`);
  }

  const data = (await response.json()) as BeTopWinGamesResponse;
  return mapTopWinsResponse(data, currency);
};
